/**Import react section */
import React, { useState } from 'react'
import propTypes from 'prop-types'
import moment from 'moment'

/** Material UI components */
import {
	Button,
	ButtonGroup,
	CircularProgress,
	IconButton,
	Popper,
	ClickAwayListener,
	Typography,
	Paper,
	Tooltip,
	List,
	ListItem,
	ListItemText,
	Badge,
	ListSubheader
} from '@material-ui/core/'
import NotificationsIcon from '@material-ui/icons/Notifications'
import GetAppIcon from '@material-ui/icons/GetApp'
import DeleteForeverIcon from '@material-ui/icons/DeleteForever'
import { MonetizationOn } from '@material-ui/icons'
import { MailOutline } from '@material-ui/icons'

/**Import resources section */
import { twoColumnsStyle as useStyles } from '../../resources/styles/LayoutStyles'

/**
 * Notifications panel rendered in the authenticated toolbar.
 *
 * Presentational: it renders the collection it is given, applies no filter of its own,
 * and reports the selected notification back through [onNotificationRequestView].
 */
const NotificationsPanel = (props) => {
	const classes = useStyles()
	const [anchorEl, setAnchorEl] = useState(null)
	const {
		notifications,
		onNotificationRequestView,
		notificationType = 'general',
		onDownload,
		isDownloading,
		onClearAll,
		isClearing
	} = props

	/** The action bar exists only when the caller supplies at least one action handler */
	const hasActions = Boolean(onDownload || onClearAll)
	const hasNotifications = notifications.length !== 0

	/**
	 * On redirect view notification request
	 * @param {Object} notification - notification object
	 */
	const onNotificationView = (notification) => {
		onNotificationRequestView(notification)
		setAnchorEl(null)
	}

	const getNotificationConfig = () => {
		switch (notificationType) {
			case 'payment':
				return {
					title: 'Payment Notifications',
					icon: <MonetizationOn style={{ color: '#FCCB56' }} />,
					headerTitle: 'PAYMENT NOTIFICATIONS'
				}
			case 'email':
				return {
					title: 'Email Notifications',
					icon: <MailOutline style={{ color: '#ffffffff' }} />,
					headerTitle: 'EMAIL NOTIFICATIONS'
				}
			default:
				return {
					title: 'Notifications',
					icon: <NotificationsIcon style={{ color: '#FFFF' }} />,
					headerTitle: 'NOTIFICATIONS'
				}
		}
	}

	const config = getNotificationConfig()

	const renderNotificationContent = (notification) => {
		switch (notification.RequestType) {
			case 'PaymentRequest':
				return (
					<ListItemText
						primary={<Typography variant='caption'>{`PAYMENT REQUEST: ${notification.Folio} | ${notification.Description}`}</Typography>}
						secondary={`Send: ${moment(notification.CreationDate).format('MM/DD/YYYY, h:mm:ss a')}`}
					/>
				)
			case 'Service':
				return (
					<ListItemText
						primary={
							<Typography variant='caption'>{`SERVICE WITH TICKET: ${notification.Folio} | ${notification.Description}`}</Typography>
						}
						secondary={`Send: ${moment(notification.CreationDate).format('MM/DD/YYYY, h:mm:ss a')}`}
					/>
				)
			case 'EmailService':
				return (
					<ListItemText
						primary={
							<Typography variant='caption'>
								{`EMAIL IN SERVICE WITH TICKET: ${notification.Folio} | ${notification.Description}`}
							</Typography>
						}
						secondary={`Received: ${moment(notification.CreationDate).format('MM/DD/YYYY, h:mm:ss a')}`}
					/>
				)
			default:
				return (
					<ListItemText
						primary={<Typography variant='caption'>{notification.Description || 'General Notification'}</Typography>}
						secondary={`Date: ${moment(notification.CreationDate).format('MM/DD/YYYY, h:mm:ss a')}`}
					/>
				)
		}
	}

	return (
		<div>
			<Tooltip component='span' title={config.title}>
				<IconButton edge='start' size='medium' onClick={(event) => setAnchorEl(event.currentTarget)}>
					<Badge
						badgeContent={notifications.length}
						color='error'
						anchorOrigin={{
							vertical: 'top',
							horizontal: 'left'
						}}
					>
						{config.icon}
					</Badge>
				</IconButton>
			</Tooltip>

			<Popper className={classes.notificationPaper} anchorEl={anchorEl} open={anchorEl !== null}>
				<ClickAwayListener onClickAway={() => setAnchorEl(null)}>
					<Paper elevation={2}>
						<List
							dense
							className={classes.contentNotifications}
							subheader={
								<ListSubheader component='div' id='nested-list-subheader' className={classes.rootTitle}>
									{hasActions ? (
										<div className={classes.notificationsHeaderRow}>
											<strong>{config.headerTitle}</strong>
											<div className={classes.notificationsHeaderActions}>
												{onDownload && (
													<ButtonGroup
														component='span'
														size='small'
														variant='outlined'
														color='primary'
														className={classes.notificationsActionGroup}
													>
														<Tooltip title='Download pending notifications'>
															<Button
																className={classes.notificationsActionButton}
																disabled={!hasNotifications || isDownloading}
																onClick={onDownload}
															>
																{isDownloading ? <CircularProgress size={18} /> : <GetAppIcon />}
															</Button>
														</Tooltip>
													</ButtonGroup>
												)}
												{onClearAll && (
													<Tooltip component='span' title='Delete all notifications'>
														<IconButton
															size='small'
															className={classes.notificationsDeleteButton}
															disabled={!hasNotifications || isClearing}
															onClick={onClearAll}
														>
															{isClearing ? <CircularProgress size={18} /> : <DeleteForeverIcon />}
														</IconButton>
													</Tooltip>
												)}
											</div>
										</div>
									) : (
										<strong>{config.headerTitle}</strong>
									)}
								</ListSubheader>
							}
						>
							{notifications.map((notification) => (
								<ListItem
									key={notification.Token || notification.id}
									divider
									button
									className={classes.outlinedRoot}
									classes={{
										root: classes.outlinedRoot,
										selected: classes.outlinedRoot
									}}
									onClick={() => onNotificationView(notification)}
								>
									{renderNotificationContent(notification)}
								</ListItem>
							))}

							{notifications.length === 0 && (
								<ListItem key='no-notifications' divider>
									<ListItemText
										secondary={
											notificationType === 'email'
												? 'No new emails'
												: notificationType === 'payment'
													? 'No pending payment requests'
													: 'No pending notifications'
										}
									/>
								</ListItem>
							)}
						</List>
					</Paper>
				</ClickAwayListener>
			</Popper>
		</div>
	)
}

NotificationsPanel.propTypes = {
	notifications: propTypes.array.isRequired,
	onNotificationRequestView: propTypes.func.isRequired,
	notificationType: propTypes.oneOf(['general', 'payment', 'email']),
	/** Supplied by the caller that owns the download action; its presence renders the action bar */
	onDownload: propTypes.func,
	/** True while the download request is in flight, so the action cannot be re-triggered */
	isDownloading: propTypes.bool,
	/** Supplied by the caller that owns the deletion; its presence renders the action bar */
	onClearAll: propTypes.func,
	/** True while the deletion is in flight, so the action cannot be re-triggered */
	isClearing: propTypes.bool
}

// `notifications` and `onNotificationRequestView` are deliberately absent: React resolves
// defaultProps before validating propTypes, so giving a required prop a default means the
// validator never sees `undefined` and the isRequired warning never fires. A consumer that
// forgot one would learn about it from a console.warn at click time, or not at all.
NotificationsPanel.defaultProps = {
	notificationType: 'general',
	onDownload: null,
	isDownloading: false,
	onClearAll: null,
	isClearing: false
}

export default NotificationsPanel
